import { vendorName, type CatalogOffer } from '../services/catalog-api';

/**
 * What the Items table shows about an item's offers: how many vendors offer it and the price range of the ACTIVE offers, with the
 * preferred vendor's price singled out.
 */
export interface OfferSummary {
  vendorCount: number;
  min: number | null;
  max: number | null;
  preferred: CatalogOffer | null;
}

export function summarizeOffers(offers: CatalogOffer[] | undefined): OfferSummary {
  const active = (offers ?? []).filter((offer) => offer.is_active);
  const prices = active.map((offer) => offer.unit_price);
  return {
    vendorCount: new Set(active.map((offer) => offer.vendor_id)).size,
    min: prices.length ? Math.min(...prices) : null,
    max: prices.length ? Math.max(...prices) : null,
    preferred: active.find((offer) => offer.is_preferred) ?? null,
  };
}

export const formatPrice = (value: number): string => new Intl.NumberFormat('en-US', { style: 'currency', currency: 'USD', minimumFractionDigits: 2, maximumFractionDigits: 4 }).format(value);

/** "$138.00 – $159.00", "$145.00" (one price), or '-' when there is none. */
export function priceRangeLabel(summary: OfferSummary): string {
  if (summary.min === null || summary.max === null) return '-';
  return summary.min === summary.max ? formatPrice(summary.min) : `${formatPrice(summary.min)} – ${formatPrice(summary.max)}`;
}

/**
 * One vendor row in the item popup. Rows live in the form until the item is saved: a new row has no `id`, an existing one carries the
 * id of its offer. Nothing is written until the single Save of the popup.
 */
export interface OfferRow {
  /** stable React key (a new row has no id yet) */
  key: string;
  id: string | null;
  vendorId: string;
  vendorLabel: string;
  unitPrice: string;
  vendorSku: string;
  isActive: boolean;
  isPreferred: boolean;
}

let newRowCounter = 0;
export const newOfferRow = (fields: Pick<OfferRow, 'vendorId' | 'vendorLabel' | 'unitPrice' | 'vendorSku'>): OfferRow => ({
  key: `new-${++newRowCounter}`, id: null, isActive: true, isPreferred: false, ...fields,
});

export const rowOfOffer = (offer: CatalogOffer): OfferRow => ({
  key: offer.id,
  id: offer.id,
  vendorId: offer.vendor_id,
  vendorLabel: vendorName(offer.vendor),
  unitPrice: String(offer.unit_price),
  vendorSku: offer.vendor_sku ?? '',
  isActive: offer.is_active,
  isPreferred: offer.is_preferred,
});

/** Why these rows cannot be saved, or null. */
export function offerRowsProblem(rows: OfferRow[]): string | null {
  for (const row of rows) {
    const price = row.unitPrice.trim();
    if (!row.id && !row.vendorId) return 'Select a vendor';
    if (price === '' || !Number.isFinite(Number(price)) || Number(price) < 0) return `Enter a unit price (0 or more) for ${row.vendorLabel || 'each vendor'}`;
  }
  if (rows.filter((row) => row.isPreferred && row.isActive).length > 1) return 'Only one vendor can be the preferred one';
  return null;
}

/**
 * What the API receives: every NEW row, the existing rows that changed (price, vendor SKU, active, preferred) and a `{ id, remove: true }`
 * for each saved offer taken off the item. Rows that did not change are not sent, so saving an item never rewrites an offer nobody touched.
 * A row that was never saved and was removed is simply not in `rows`: it never reaches the server.
 */
export function offersPayload(rows: OfferRow[], original: CatalogOffer[], removedIds: string[] = []): Array<Record<string, unknown>> {
  const before = new Map(original.map((offer) => [offer.id, offer]));
  const payload: Array<Record<string, unknown>> = removedIds.map((id) => ({ id, remove: true }));
  for (const row of rows) {
    const body = { unit_price: Number(row.unitPrice), vendor_sku: row.vendorSku, is_active: row.isActive, is_preferred: row.isPreferred && row.isActive };
    if (!row.id) {
      payload.push({ vendor_id: row.vendorId, ...body });
      continue;
    }
    const stored = before.get(row.id);
    const changed = !stored || stored.unit_price !== body.unit_price || (stored.vendor_sku ?? '') !== body.vendor_sku.trim() || stored.is_active !== body.is_active || stored.is_preferred !== body.is_preferred;
    if (changed) payload.push({ id: row.id, ...body });
  }
  return payload;
}
